import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/admin/api";
import { fmtDate } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { ShieldAlert, ChevronDown, ChevronUp, Trash2, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const SpamFolder = ({ onRestored }) => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/spam");
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const purge = async () => {
    if (!window.confirm(`Permanently delete all ${items.length} spam submissions?`)) return;
    setBusy(true);
    try {
      await api.delete("/admin/spam");
      toast.success("Spam folder emptied");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const restore = async (id) => {
    try {
      await api.post(`/admin/spam/${id}/restore`);
      toast.success("Moved to inquiries");
      load();
      onRestored?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/spam/${id}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const count = items?.length ?? 0;

  return (
    <div data-testid="spam-folder" className="mt-8 bg-white rounded-2xl border border-bone overflow-hidden">
      <button type="button" data-testid="spam-folder-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-sand/60 transition-colors">
        <span className="flex items-center gap-2 text-ink font-medium">
          <ShieldAlert size={18} className="text-terracotta" /> Spam folder
          <span data-testid="spam-count" className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-bone text-ink-soft">{count}</span>
        </span>
        {open ? <ChevronUp size={18} className="text-ink-soft" /> : <ChevronDown size={18} className="text-ink-soft" />}
      </button>
      {open && (
        <div className="border-t border-bone">
          <div className="flex items-center justify-between px-5 py-3 bg-sand/40">
            <p className="text-xs text-ink-soft">Blocked by bot protection. These never triggered an email. Amber "Possible" items matched only weak patterns — glance at them and restore anything real (restoring sends the normal emails).</p>
            {count > 0 && (
              <Button data-testid="spam-purge" size="sm" variant="outline" onClick={purge} disabled={busy}
                className="rounded-full border-red-200 text-red-600 hover:bg-red-50 gap-1.5 whitespace-nowrap">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} Empty spam
              </Button>
            )}
          </div>
          {!items ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-terracotta" /></div>
          ) : count === 0 ? (
            <p data-testid="spam-empty" className="text-center text-ink-soft py-10 text-sm">Nothing caught yet.</p>
          ) : (
            <ul className="divide-y divide-bone">
              {items.map((it) => (
                <li key={it.id} data-testid={`spam-row-${it.id}`} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink truncate">
                      {it.spam_level === "possible" && <span data-testid={`spam-possible-${it.id}`} className="inline-flex items-center px-2 py-0.5 mr-2 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 align-middle">Possible — review</span>}
                      {it.first_name} <span className="text-ink-soft font-normal">· {it.email}</span>
                    </p>
                    {it.notes && <p className="text-xs text-ink-soft truncate">{it.notes}</p>}
                    <p className="text-xs text-terracotta mt-0.5">{(it.spam_reasons || []).join(", ")} · {fmtDate(it.created_at)}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`spam-restore-${it.id}`} size="sm" variant="outline" onClick={() => restore(it.id)} className="rounded-full border-ink/20 gap-1.5">
                      <Undo2 size={14} /> Not spam
                    </Button>
                    <Button data-testid={`spam-delete-${it.id}`} size="sm" variant="ghost" onClick={() => remove(it.id)} className="rounded-full text-red-600 hover:bg-red-50">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
